import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { PayoutDraftsItem } from 'api';
import {
  getGetDisbursementPayoutDraftsQueryKey,
  useGetCustomerCurrentUser,
  useGetDisbursementPayoutDrafts
} from 'api';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';
import { useGlobalFilters } from 'hooks/useGlobalFilters';
import { useVirtualList } from 'hooks/useVirtualList';

import { DataTable } from 'components/ui/organisms/DataTable';

import { apiParams } from './apiAdapter';
import { getColumns } from './columns';
import {
  PAYOUT_DRAFTS_TABLE_ROW_ENTRY_TEST_ID,
  PAYOUT_DRAFTS_TABLE_TEST_ID
} from './constants';
import type { LocalState } from './localState';
import { PayoutDraftDetails } from './PayoutDraftDetails';

type Props = {
  localState: LocalState;
  updateCursorAfter: (value: string) => void;
};

export const PayoutDraftsTable = ({ localState, updateCursorAfter }: Props) => {
  const organisationId = useOrganisationId<number>();
  const queryClient = useQueryClient();

  const [selectedRowData, setSelectedRowData] = useState<PayoutDraftsItem>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const { findAccountByAccountNumber } = useVirtualAccounts();
  const { filters: globalFilters } = useGlobalFilters();

  const payoutDraftRequestColumns = useMemo(
    () => getColumns(findAccountByAccountNumber),
    [findAccountByAccountNumber]
  );

  const { data: payoutDrafts, isFetching } = useGetDisbursementPayoutDrafts(
    organisationId,
    apiParams({
      ...localState,
      ...globalFilters
    })
  );

  const { data: currentUser } = useGetCustomerCurrentUser();

  const { onScroll, data, removeElement } = useVirtualList<PayoutDraftsItem>({
    data: payoutDrafts?.data,
    initialRefValue: payoutDrafts?.meta?.cursor_next,
    uniqueKey: 'id',
    isFetching,
    updateCursorCallback: updateCursorAfter,
    resetConditions: [globalFilters, localState.filters, updateCursorAfter],
    cursorNext: payoutDrafts?.meta?.has_next_page
      ? payoutDrafts.meta?.cursor_next
      : undefined
  });

  const handleRowClick = (record: PayoutDraftsItem) => {
    setSelectedRowData((selected) =>
      selected !== record ? record : undefined
    );
  };

  const handleRemoveElement = (elem: PayoutDraftsItem) => {
    removeElement(elem);
    /**
     * the mutation is not instant, and we might get stale data if we do not invalidate the query.
     * Invalidating the query also queries the server for the latest data.
     * And by doing so, we get the latest meta.total count and do not have to update it manually.
     */
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: getGetDisbursementPayoutDraftsQueryKey(organisationId)
      });
    }, 3000);
  };

  return (
    <DataTable
      total={payoutDrafts?.meta?.total}
      dataSource={data}
      columns={payoutDraftRequestColumns}
      onScroll={onScroll}
      onRowClick={handleRowClick}
      isLoading={isFetching}
      rowKey="id"
      selectedRow={selectedRowData}
      tableDataTestId={PAYOUT_DRAFTS_TABLE_TEST_ID}
      rowDataTestId={PAYOUT_DRAFTS_TABLE_ROW_ENTRY_TEST_ID}
      rightPanel={
        selectedRowData ? (
          <PayoutDraftDetails
            payoutDraftId={selectedRowData.id!}
            currentUser={currentUser}
            onClose={() => setSelectedRowData(undefined)}
            onRemoveElementFromTable={() =>
              handleRemoveElement(selectedRowData)
            }
          />
        ) : undefined
      }
    />
  );
};
